/**
 * Vehicle profile hook for the VehicleDetail deep-dive page (PR 5.5).
 *
 * Hits `GET /api/v1/vehicles/by-token/:asset_token` — the
 * UUID-addressed sibling of the existing `/vehicles/:registration`
 * endpoint. The new path was added in this PR specifically because
 * `/{registration}` and `/{asset_token}` would collide at the routing
 * layer (FastAPI matches path *patterns*, not parameter types — a
 * `/{x}` route shadows another `/{x}` regardless of converter).
 *
 * The hook is `enabled` only when an `assetToken` is supplied so the
 * page can call it unconditionally from the route-param hook without
 * firing a 404 against the placeholder string.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { VehicleProfile } from '@/types/vehicles';

const STALE_MS = 60_000;

export function useVehicleProfile(
  assetToken: string | undefined,
): UseQueryResult<VehicleProfile, Error> {
  return useQuery<VehicleProfile, Error>({
    queryKey: ['vehicle-profile', assetToken],
    enabled: Boolean(assetToken),
    queryFn: async () => {
      const { data } = await api.get<VehicleProfile>(
        `/vehicles/by-token/${encodeURIComponent(assetToken as string)}`,
      );
      return data;
    },
    staleTime: STALE_MS,
  });
}
